import './perks-router.component.scss';

import { h } from 'preact';
import { Redirect, Route, Switch } from 'wouter-preact';

import { selectIsAuthenticated } from '../../../auth/store/auth.selectors';
import { useFeatureFlag } from '../../../feature-flags/hooks/use-feature-flag.hook';
import { FeatureFlagName } from '../../../feature-flags/models/feature-flag-names.enum';
import { useTranslation } from '../../../i18n';
import { useAppSelector } from '../../../store/hooks';
import { getSearchParams } from '../../../utils/window.utils';
import { PerksAccountComponent } from '../perks-account/perks-account.component';
import { PerksCodesComponent } from '../perks-codes/perks-codes.component';
import { PerksCollectiblesComponent } from '../perks-collectibles/perks-collectibles.component';
import { PerksCustomContentComponent } from '../perks-custom-content/perks-custom-content.component';
import { PerksHomeComponent } from '../perks-home/perks-home.component';
import { PerksJoinComponent } from '../perks-join/perks-join.component';
import { PerksMerchComponent } from '../perks-merch/perks-merch.component';
import { PerksMusicComponent } from '../perks-music/perks-music.component';
import { PerksMusicDetailComponent } from '../perks-music/perks-music-detail/perks-music-detail.component';
import { PerksPostInfoComponent } from '../perks-posts/perks-post-info/perks-post-info.component';
import { PerksPostsComponent } from '../perks-posts/perks-posts.component';
import { PerksVideosComponent } from '../perks-videos/perks-videos.component';

// Declared at module level, not inside PerksRouter: a component defined in a render is a new type
// on every render, so each re-render of the perks wrapper (a filter change, a loading flag) threw
// away the routed page and mounted a fresh one.
const ProtectedRoute = ({ component: Component, path, children }: any) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const hasAccessCode = !!getSearchParams()?.get('access');
  return (
    <Route path={path}>
      {(params: any) => {
        if (!isAuthenticated && !hasAccessCode) {
          return <Redirect to="/join" />;
        }
        return children ? children(params) : <Component />;
      }}
    </Route>
  );
};

export const PerksRouter = () => {
  const { t } = useTranslation();
  const isAudioStreamingEnabled = useFeatureFlag(FeatureFlagName.AUDIO_STREAMING);

  return (
    <Switch>
      <Route path="/" component={PerksHomeComponent} />
      <ProtectedRoute path="/posts" component={PerksPostsComponent} />
      <Route path="/posts/:id">{(params: any) => <PerksPostInfoComponent id={params.id} />}</Route>
      <ProtectedRoute path="/merch" component={PerksMerchComponent} />
      <ProtectedRoute path="/videos" component={PerksVideosComponent} />
      <Route path="/music">
        {() => (isAudioStreamingEnabled ? <PerksMusicComponent /> : null)}
      </Route>
      <Route path="/music/:id">
        {(params: any) => (isAudioStreamingEnabled ? <PerksMusicDetailComponent albumId={params.id} /> : null)}
      </Route>
      <ProtectedRoute path="/codes" component={PerksCodesComponent} />
      <ProtectedRoute path="/collectibles" component={PerksCollectiblesComponent} />
      <ProtectedRoute path="/pages/:id">
        {(params: any) => <PerksCustomContentComponent id={params.id} />}
      </ProtectedRoute>
      <ProtectedRoute path="/my-account" component={PerksAccountComponent} />
      <Route path="/join">{() => <PerksJoinComponent />}</Route>
      <Route>
        <div class="single-not-found">
          <h1>{t('perks.oops')}</h1>
          <h4>{t('perks.notFound')}</h4>
          <button class="btn button" onClick={() => window.history.back()}>
            {t('perks.goBack')}
          </button>
        </div>
      </Route>
    </Switch>
  );
};
